import React from "react"

import { type LeadNote } from "../../types/lead.types.ts"
import { NoteCard } from "./NoteCard.tsx"

interface NotesBoardViewProps {
  notes: LeadNote[]
  canEdit?: boolean
  onEdit: (note: LeadNote) => void
  onDelete: (id: string) => void
  onTogglePin: (note: LeadNote) => void
}

/**
 * Bespoke Tailwind grid. Column count is driven by available width via
 * `auto-fill`/`minmax` rather than fixed breakpoints, so a note never gets
 * squeezed narrower than it can comfortably hold two or three lines of text
 * — around 3-4 cards wide on a typical desktop viewport, 2-3 at medium
 * widths, 1 on narrow/mobile. `items-start` (not the grid default `stretch`)
 * is what lets a short note stay short instead of being stretched to match
 * the tallest card in its row. No shared card-grid component fit a
 * sticky-note board, so this is built standalone.
 */
function NotesBoardView({
  notes,
  canEdit,
  onEdit,
  onDelete,
  onTogglePin
}: NotesBoardViewProps) {
  return (
    <div
      data-testid={"notes-board-view"}
      className={
        "grid grid-cols-[repeat(auto-fill,minmax(260px,1fr))] items-start gap-4"
      }
    >
      {notes.map((note) => (
        <NoteCard
          key={note.id}
          note={note}
          canEdit={canEdit}
          onEdit={onEdit}
          onDelete={onDelete}
          onTogglePin={onTogglePin}
        />
      ))}
    </div>
  )
}

export { NotesBoardView }
